import { Network, Globe, Server, Wifi, Plus, Layers } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, ValidatedInput, Button } from '../../ui';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { validateCIDR, validateIPv4, validateDomain } from '../../../utils/validation';
import { useMemo } from 'react';
import { NetworkSegmentCard, SubscriptionModelToggle } from '../../network';

const NETWORK_PRESETS = [
  { label: '192.168.200.x', subnet: '192.168.200.0/24', gateway: '192.168.200.1', netmask: '255.255.255.0' },
  { label: '10.0.0.x', subnet: '10.0.0.0/24', gateway: '10.0.0.1', netmask: '255.255.255.0' },
  { label: '172.16.0.x', subnet: '172.16.0.0/24', gateway: '172.16.0.1', netmask: '255.255.255.0' },
];

export function NetworkStep() {
  const {
    config,
    updateNetwork,
    addNetworkSegment,
    updateNetworkSegment,
    removeNetworkSegment,
    setSubscriptionModel,
  } = useConfigurator();
  const { network } = config;

  // Parse IP ranges for visualization
  const ipRanges = useMemo(() => {
    const base = network.subnet.split('/')[0].split('.').slice(0, 3).join('.');
    const dhcpStart = parseInt(network.dhcpRangeStart.split('.')[3]) || 100;
    const dhcpEnd = parseInt(network.dhcpRangeEnd.split('.')[3]) || 199;
    const nodeIPs = config.nodes.map((n) => parseInt(n.ip.split('.')[3]));

    return { base, dhcpStart, dhcpEnd, nodeIPs };
  }, [network, config.nodes]);

  const applyPreset = (preset: (typeof NETWORK_PRESETS)[0]) => {
    const base = preset.subnet.split('/')[0].split('.').slice(0, 3).join('.');
    updateNetwork({
      subnet: preset.subnet,
      gateway: preset.gateway,
      netmask: preset.netmask,
      dhcpRangeStart: `${base}.100`,
      dhcpRangeEnd: `${base}.199`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Network Configuration</h2>
          <p className="mt-1 text-gray-500">
            Configure your airgapped network topology. All settings enforce isolation by default.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Quick:</span>
          {NETWORK_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant="outline"
              size="sm"
              onClick={() => applyPreset(preset)}
              className="text-xs"
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Subscription Model Toggle */}
      <SubscriptionModelToggle
        subscriptionModel={network.subscriptionModel}
        onUpdate={setSubscriptionModel}
      />

      {/* Network Segments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-hermit-600" />
              <CardTitle>Network Segments</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => addNetworkSegment('administration')}
              >
                <Plus className="mr-1 h-3 w-3" />
                Admin
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addNetworkSegment('user')}
              >
                <Plus className="mr-1 h-3 w-3" />
                User
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addNetworkSegment('monitoring')}
              >
                <Plus className="mr-1 h-3 w-3" />
                Monitor
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {network.segments.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
              <Layers className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                No network segments configured. Add segments to separate admin, user, and monitoring traffic.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Each segment gets its own VLAN and subnet for isolation.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {network.segments.map((segment) => (
                <NetworkSegmentCard
                  key={segment.id}
                  segment={segment}
                  onUpdate={(updates) => updateNetworkSegment(segment.id, updates)}
                  onRemove={() => removeNetworkSegment(segment.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Primary Network */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Network className="h-5 w-5 text-hermit-600" />
              <CardTitle>Primary Network</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ValidatedInput
              label="Subnet (CIDR)"
              value={network.subnet}
              onChange={(value) => updateNetwork({ subnet: value })}
              validate={validateCIDR}
              placeholder="192.168.200.0/24"
              hint="The isolated network range for all nodes"
            />
            <ValidatedInput
              label="Gateway IP"
              value={network.gateway}
              onChange={(value) => updateNetwork({ gateway: value })}
              validate={validateIPv4}
              placeholder="192.168.200.1"
              hint="IP address of the gateway router"
            />
            <ValidatedInput
              label="Netmask"
              value={network.netmask}
              onChange={(value) => updateNetwork({ netmask: value })}
              validate={validateIPv4}
              placeholder="255.255.255.0"
            />
          </CardContent>
        </Card>

        {/* DNS Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-hermit-600" />
              <CardTitle>DNS Settings</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ValidatedInput
              label="Local Domain"
              value={network.domain}
              onChange={(value) => updateNetwork({ domain: value })}
              validate={validateDomain}
              placeholder="hermit.local"
              hint="All nodes will be reachable as hostname.domain"
            />
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                <strong>Airgap Notice:</strong> No upstream DNS servers are configured.
                Only local *.{network.domain} names will resolve.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* DHCP Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-hermit-600" />
              <CardTitle>DHCP Range</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ValidatedInput
              label="Range Start"
              value={network.dhcpRangeStart}
              onChange={(value) => updateNetwork({ dhcpRangeStart: value })}
              validate={validateIPv4}
              placeholder="192.168.200.100"
              hint="Start of DHCP lease range (for new nodes)"
            />
            <ValidatedInput
              label="Range End"
              value={network.dhcpRangeEnd}
              onChange={(value) => updateNetwork({ dhcpRangeEnd: value })}
              validate={validateIPv4}
              placeholder="192.168.200.199"
            />
          </CardContent>
        </Card>

        {/* Network Summary */}
        <Card className="bg-gray-50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wifi className="h-5 w-5 text-hermit-600" />
              <CardTitle>Network Summary</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Network:</dt>
                <dd className="font-mono text-gray-900">{network.subnet}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Gateway:</dt>
                <dd className="font-mono text-gray-900">{network.gateway}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Domain:</dt>
                <dd className="font-mono text-gray-900">{network.domain}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">DHCP Pool:</dt>
                <dd className="font-mono text-gray-900">
                  {network.dhcpRangeStart} - {network.dhcpRangeEnd.split('.').pop()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Static IPs:</dt>
                <dd className="font-mono text-gray-900">
                  .1 - .99 (reserved for nodes)
                </dd>
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Internet Access:</dt>
                  <dd className="font-medium text-red-600">Disabled (Airgap)</dd>
                </div>
              </div>
            </dl>

            {/* Visual IP Range */}
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="text-xs text-gray-500 mb-2">IP Address Map ({ipRanges.base}.x)</div>
              <div className="h-6 rounded-full bg-gray-200 overflow-hidden flex">
                {/* Gateway (1) */}
                <div
                  className="bg-orange-400"
                  style={{ width: '0.4%' }}
                  title=".1 Gateway"
                />
                {/* Static range (2-99) */}
                <div
                  className="bg-blue-400 relative"
                  style={{ width: '38.4%' }}
                  title=".2-.99 Static nodes"
                >
                  {/* Node dots */}
                  {ipRanges.nodeIPs
                    .filter((ip) => ip > 1 && ip < 100)
                    .map((ip) => (
                      <div
                        key={ip}
                        className="absolute top-0 bottom-0 w-1 bg-blue-700"
                        style={{ left: `${((ip - 2) / 98) * 100}%` }}
                        title={`.${ip}`}
                      />
                    ))}
                </div>
                {/* DHCP range (100-199) */}
                <div
                  className="bg-green-400"
                  style={{ width: '39.2%' }}
                  title=".100-.199 DHCP"
                />
                {/* Reserved (200-254) */}
                <div
                  className="bg-gray-300"
                  style={{ width: '21.6%' }}
                  title=".200-.254 Reserved"
                />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>.1 GW</span>
                <span>.2-99 Static</span>
                <span>.100-199 DHCP</span>
                <span>.200+ Reserved</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
